import React, { useCallback, useEffect } from "react";
import { AlertCircle, CheckCircle, Settings, Shield, X } from "lucide-react";

export function BaselineConfirmationPopup({ deviceName, msbName, onSubmit, onCancel, closePopup }) {
    const handleClose = useCallback(() => {
        onCancel && onCancel();
        closePopup();
    }, [onCancel, closePopup]);

    useEffect(() => {
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [handleClose]);

    const handleConfirm = () => {
        onSubmit && onSubmit();
        closePopup();
    };

    return (
        <div className="oct-apply-baseline-modal" role="dialog" aria-modal="true" aria-labelledby="oct-apply-baseline-title">
            <div className="oct-apply-baseline-modal__backdrop" onClick={handleClose} />

            <div className="oct-apply-baseline-modal__dialog" onClick={(event) => event.stopPropagation()}>
                <div className="oct-apply-baseline-modal__header">
                    <div className="oct-apply-baseline-modal__header-glow" />

                    <div className="oct-apply-baseline-modal__title-group">
                        <div className="oct-apply-baseline-modal__title-icon">
                            <Settings size={22} />
                        </div>

                        <h4 id="oct-apply-baseline-title" className="oct-apply-baseline-modal__title">
                            Apply Baseline
                        </h4>
                    </div>

                    <button
                        type="button"
                        className="oct-apply-baseline-modal__close"
                        onClick={handleClose}
                        aria-label="Close apply baseline popup"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="oct-apply-baseline-modal__body">
                    <div className="oct-apply-baseline-modal__hero">
                        <div className="oct-apply-baseline-modal__hero-shadow" />
                        <div className="oct-apply-baseline-modal__hero-icon">
                            <Shield size={38} strokeWidth={2.4} />
                        </div>
                    </div>

                    <div className="oct-apply-baseline-modal__content">
                        <h5 className="oct-apply-baseline-modal__heading">Confirm Baseline Application</h5>

                        <p className="oct-apply-baseline-modal__message">
                            This process will apply on the device{" "}
                            <strong>{deviceName}</strong>{" "}
                            the baseline settings defined under remediation in each rule of MSB:{" "}
                            <strong>{msbName}</strong>.
                        </p>
                    </div>

                    <div className="oct-apply-baseline-modal__info-list">
                        <div className="oct-apply-baseline-modal__info oct-apply-baseline-modal__info--primary">
                            <CheckCircle size={18} className="oct-apply-baseline-modal__info-icon" />
                            <p>
                                <strong>Validation Process:</strong> Will be triggered automatically after the baseline application is completed.
                            </p>
                        </div>

                        <div className="oct-apply-baseline-modal__info oct-apply-baseline-modal__info--warning">
                            <AlertCircle size={18} className="oct-apply-baseline-modal__info-icon" />
                            <p>
                                <strong>Important:</strong> This action may temporarily affect device availability during the remediation process.
                            </p>
                        </div>
                    </div>

                    <div className="oct-apply-baseline-modal__details">
                        <h6 className="oct-apply-baseline-modal__details-title">Target Configuration</h6>

                        <div className="oct-apply-baseline-modal__detail-row">
                            <span>Device:</span>
                            <strong>{deviceName}</strong>
                        </div>

                        <div className="oct-apply-baseline-modal__detail-row">
                            <span>Baseline:</span>
                            <strong>{msbName}</strong>
                        </div>

                        <div className="oct-apply-baseline-modal__detail-row">
                            <span>Action:</span>
                            <strong className="oct-apply-baseline-modal__detail-accent">Apply &amp; Validate</strong>
                        </div>
                    </div>

                    <p className="oct-apply-baseline-modal__confirmation">Please confirm to proceed?</p>
                </div>

                <div className="oct-apply-baseline-modal__footer">
                    <button
                        type="button"
                        className="btn oct-apply-baseline-modal__btn oct-apply-baseline-modal__btn--secondary"
                        onClick={handleClose}
                    >
                        Close
                    </button>

                    <button
                        type="button"
                        className="btn oct-apply-baseline-modal__btn oct-apply-baseline-modal__btn--primary"
                        onClick={handleConfirm}
                    >
                        <CheckCircle size={16} />
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}
